import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { REDEEM_DEATH_FINALE_MS } from './useRedeemDeathSequence';
import { LethalDeathParticleDissipation } from './LethalDeathParticleDissipation';
import './RedeemDeathDissipation.css';

export const REDEEM_GHOST_IMAGE_SRC = '/images/redeem_ghost.png';
export const REDEEM_GHOST_SIGH_SOUND = '/sounds/redeem_sigh.mp3';
export const REDEEM_GHOST_SIGH_GAIN = 0.65;

/** Matches .order-design-redeem-ghost-host > .order-design-redeem-ghost-art on-card layout */
const GHOST_WIDTH_RATIO = 0.78;
const GHOST_HEIGHT_RATIO = 0.88;
const GHOST_CENTER_Y_RATIO = 0.52;

export function playRedeemGhostSighSound() {
  playSoundEffect(REDEEM_GHOST_SIGH_SOUND, REDEEM_GHOST_SIGH_GAIN);
}

function measureGhostFlyRect(imgEl, instanceId) {
  if (!imgEl) {
    throw new Error('measureGhostFlyRect: imgEl is required');
  }

  const rect = imgEl.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }

  const host = imgEl.closest('.order-design-redeem-ghost-host');
  if (!host) {
    throw new Error(`measureGhostFlyRect: ghost host not found for instance ${instanceId}`);
  }
  const hostRect = host.getBoundingClientRect();
  if (hostRect.width <= 0 || hostRect.height <= 0) {
    throw new Error(`measureGhostFlyRect: invalid host rect for instance ${instanceId}`);
  }

  const width = hostRect.width * GHOST_WIDTH_RATIO;
  const height = hostRect.height * GHOST_HEIGHT_RATIO;
  const left = hostRect.left + hostRect.width * 0.5 - width * 0.5;
  const top = hostRect.top + hostRect.height * 0.5 - height * GHOST_CENTER_Y_RATIO;
  return { left, top, width, height };
}

/**
 * On-card ghost while marked; on finale, capture viewport anchor from the on-card
 * render, then portal a single element that rises/scales/dissipates in place.
 */
export function RedeemGhostMarkOverlay({
  show,
  ascend = false,
  instanceId,
  durationMs = REDEEM_DEATH_FINALE_MS,
}) {
  const imgRef = useRef(null);
  const sighPlayedRef = useRef(false);
  const [flyRect, setFlyRect] = useState(null);

  useLayoutEffect(() => {
    if (!ascend) {
      setFlyRect(null);
      sighPlayedRef.current = false;
      return;
    }
    if (flyRect) {
      return;
    }
    if (!instanceId) {
      throw new Error('RedeemGhostMarkOverlay: instanceId is required when ascend started');
    }
    if (!imgRef.current) {
      throw new Error('RedeemGhostMarkOverlay: ghost element missing when ascend started');
    }

    const measured = measureGhostFlyRect(imgRef.current, instanceId);
    setFlyRect(measured);

    if (!sighPlayedRef.current) {
      sighPlayedRef.current = true;
      playRedeemGhostSighSound();
    }
  }, [ascend, flyRect, instanceId, durationMs]);

  if (!show) {
    return null;
  }
  if (!instanceId) {
    throw new Error('RedeemGhostMarkOverlay: instanceId is required');
  }

  const anchorRect = flyRect
    ? {
      left: flyRect.left,
      top: flyRect.top,
      width: flyRect.width,
      height: flyRect.height,
    }
    : null;

  const flyingGhost = flyRect && typeof document !== 'undefined'
    ? createPortal(
      <>
        <LethalDeathParticleDissipation
          anchorRect={anchorRect}
          durationMs={durationMs}
        />
        <img
          src={REDEEM_GHOST_IMAGE_SRC}
          alt=""
          className="order-design-redeem-ghost-art order-design-redeem-ghost-art--ascending"
          style={{
            position: 'fixed',
            left: `${flyRect.left}px`,
            top: `${flyRect.top}px`,
            width: `${flyRect.width}px`,
            height: `${flyRect.height}px`,
            zIndex: 10050,
            margin: 0,
            '--redeem-death-duration': `${durationMs}ms`,
          }}
          data-redeem-ghost-instance={String(instanceId)}
          data-redeem-ghost-phase="flying"
          draggable={false}
        />
      </>,
      document.body
    )
    : null;

  return (
    <>
      {!flyRect ? (
        <img
          ref={imgRef}
          src={REDEEM_GHOST_IMAGE_SRC}
          alt=""
          className="order-design-redeem-ghost-art"
          data-redeem-ghost-instance={String(instanceId)}
          data-redeem-ghost-phase="on-card"
          draggable={false}
        />
      ) : null}
      {flyingGhost}
    </>
  );
}
